import { unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { buildKeys } from "../shared/keys";

const STALE_MS = 15_000;

const agentSourcesKey = (ws: string, agentId: string) => ["ws", ws, "agents", agentId, "agent-sources"] as const;

/** `GET /v1/agents/{id}/sources`: ids of the sources this agent answers from. */
export function useAgentSourceIds(agentId: string, enabled = true) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: agentSourcesKey(ws, agentId),
    queryFn: async () => (await unwrap(api.GET("/v1/agents/{agent_id}/sources", { params: { path: { agent_id: agentId } } }))).map((source) => source.id),
    enabled,
    retry: false,
    staleTime: STALE_MS,
  });
}

interface Context {
  previous: string[] | undefined;
}

/**
 * `PUT /v1/agents/{id}/sources` with the full list. The ticked state updates at once and rolls
 * back if the server refuses; the counters ("applies to n agents", sources on the agent card) are
 * refetched afterwards.
 */
export function useSetAgentSources(agentId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const queryClient = useQueryClient();
  const key = agentSourcesKey(ws, agentId);
  return useMutation<unknown, Error, string[], Context>({
    mutationFn: (sourceIds) => unwrap(api.PUT("/v1/agents/{agent_id}/sources", { params: { path: { agent_id: agentId } }, body: { source_ids: sourceIds } })),
    onMutate: async (sourceIds) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<string[]>(key);
      queryClient.setQueryData<string[]>(key, sourceIds);
      return { previous };
    },
    onError: (_error, _ids, context) => {
      queryClient.setQueryData(key, context?.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key, exact: true });
      void queryClient.invalidateQueries({ queryKey: buildKeys.sources(ws), exact: true });
      void queryClient.invalidateQueries({ queryKey: buildKeys.agents(ws), exact: true });
      void queryClient.invalidateQueries({ queryKey: buildKeys.playgroundConfig(ws, agentId), exact: true });
    },
  });
}
